// <Studio> component incapsulates asyncronisity and performance optimizations,
// and renders a default selection of widgets.
// It provides a convenient way to use library in full capacity while retaining
// modularity and flexibility of its modules.
//
// More technically, it deals with fetching (and caching) files via network;
// and enhances the standard exposed store (which is clean and syncronious) with
// async calculations via webworkers and their cache.

import React, { Component, Fragment, StrictMode } from 'react';
// Currently, StrictMode throws warnings about react-redux;
// see https://github.com/reduxjs/react-redux/issues/897
import { createProvider, connect } from 'react-redux';
import { createStore } from 'redux';
import { STORE_KEY, SEGMENT_DURATION } from './constants';
import rootReducer from './reducers';

import EDFResource from './core/rangeResource';
import Cache from './core/exampleCache';

import { getCurrentSegmentRange } from './selectors';
import { setHeader, addDataSegment30s, removeDataSegment30s } from './actions';

import Viewport from './components/Viewport';
import Scroller from './components/TransformScroller';
import Ruler from './components/Ruler';
import Plot from './components/Plot';
import Annotations from './components/Annotations';
import SelectedAnnotation from './components/SelectedAnnotation';
import ChannelsList from './components/ChannelsList';
import AnnotationsMinimap from './components/AnnotationsMinimap';
import CtrlClickCatcher from './components/CtrlClickCatcher';
import Cursor from './components/Cursor';

// Array equality helper function.
// Nothing fancy, no support for nested arrays (we don't need it).
function equalArrays(arr1, arr2) {
  let i = arr1.length;
  if (i !== arr2.length) {
    return false;
  }

  while (i--) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

class Studio extends Component {
  constructor(props) {
    super(props);

    this.cached = null;
  }

  // handleSegmentChange() implementation depends on the assumption that it's not
  // debounced or throttled.
  handleSegmentChange = () => {
    if (!this.cached) {
      console.warn('Cache not initialized yet; ignoring update.');
      return;
    }

    // Store segmentRange at the start of the request.
    const currentSegmentRange = this.props.currentSegmentRange;

    this.cached
      .getChunksAsync(
        SEGMENT_DURATION * currentSegmentRange[0],
        SEGMENT_DURATION * currentSegmentRange.length
      )
      .then(data => {
        // Check - segmentRange might have changed (e.g. due to user navigation)
        // while we were fetching data from cache. In this case, discard data
        // altogether; there was more recent cache call, it will put proper
        // data to the store.
        //
        // This is not optimal - we might arrange to put to store those parts
        // of data which *are* in the current segment range. But in this case
        // deletion becomes a huge problem, so we avoid this, until much
        // more robust solution is designed.
        const newerSegmentRange = this.props.currentSegmentRange;
        if (!equalArrays(newerSegmentRange, currentSegmentRange)) {
          console.warn(
            'Segment range has changed while getting data; data discarded'
          );
          return;
        }

        const { data: { rawValues } } = this.props;
        for (let i = 0; i < rawValues.length; i++) {
          const iInData = currentSegmentRange.indexOf(i);
          if (iInData !== -1 && !rawValues[i]) {
            // Add segment to store if we received it from resource/cache and
            // don't have it in store already...
            this.props.dispatch(
              addDataSegment30s(
                i,
                data.slice(
                  SEGMENT_DURATION * iInData,
                  SEGMENT_DURATION * iInData + SEGMENT_DURATION
                )
              )
            );
          } else if (iInData === -1 && rawValues[i]) {
            // ...otherwise, remove from store. That's essentially garbage
            // collecting.
            this.props.dispatch(removeDataSegment30s(i));
          }
        }
      });
  };

  componentDidMount() {
    const source = { url: this.props.url };
    const options =
      typeof this.props.fetch === 'function'
        ? { fetch: this.props.fetch }
        : undefined;
    const edfResource = new EDFResource(source, options);

    edfResource
      .fetchHeader()
      .then(header => {
        // 1. Get / set header and init cache
        if (process.env.NODE_ENV === 'development') {
          console.log('App:header', header);
        }
        this.cached = new Cache(edfResource, header);
        this.props.dispatch(setHeader(header));
      })
      .then(() => {
        // 2. Ask for whatever data we need
        this.handleSegmentChange();
      });
  }

  componentDidUpdate() {
    this.handleSegmentChange();
  }

  componentWillUnmount() {
    this.cached && this.cached.destroy();
    this.cached = null;
  }

  render() {
    const children = this.props.children || (
      <Fragment>
        <Scroller>
          <Ruler />
          <Viewport>
            <Annotations />
            <Plot />
            <SelectedAnnotation />
          </Viewport>
          <Cursor />
        </Scroller>
        <CtrlClickCatcher />
        <ChannelsList />
        <AnnotationsMinimap />
      </Fragment>
    );

    return <div className="eegStudio">{children}</div>;
  }
}

const ConnectedStudio = connect(
  function mapStateToProps(state) {
    return {
      currentSegmentRange: getCurrentSegmentRange(state),
      data: state.data,
    };
  },
  undefined,
  undefined,
  {
    storeKey: STORE_KEY,
    areStatePropsEqual: function(next, prev) {
      // We only care about segmentRange updates.
      return equalArrays(next.currentSegmentRange, prev.currentSegmentRange);
    },
  }
)(Studio);

// <StudioWrapper> component makes using <Studio> easier from outside.
//
// You can either pass store to it directly, or have it connected automatically
// to existing (properly-keyed) Provider. Will not interfere with the 'main'
// redux store of an outside app, if it has any.

const StudioProvider = createProvider(STORE_KEY);

class StudioWrapper extends Component {
  constructor(props, context) {
    super(props, context);

    if (process.env.NODE_ENV === 'development') {
      if (props.store && context[STORE_KEY]) {
        console.error(
          `EEG Studio is given both 'store' prop, and '${STORE_KEY}' context (e.g. wrapped in relevant <Provider>). Choose only one way of connecting!`
        );
      }
    }

    const passedStore = props.store || context[STORE_KEY];

    if (passedStore) {
      passedStore.replaceReducer(rootReducer);
      this.store = passedStore;
    } else {
      this.store = createStore(rootReducer);
    }
  }

  render() {
    const { store: _, ...otherProps } = this.props;

    return (
      <StrictMode>
        <StudioProvider store={this.store}>
          <ConnectedStudio {...otherProps} />
        </StudioProvider>
      </StrictMode>
    );
  }
}

StudioWrapper.contextTypes = {
  [STORE_KEY]: () => null,
};

export default StudioWrapper;
